import { useState } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d762kqt4hjod66j2ejo0';

interface ConsultationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ConsultationModal({ isOpen, onClose }: ConsultationModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    const form = e.currentTarget;
    const textarea = form.querySelector('textarea[name="message"]') as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) {
      setError('Message must be 500 characters or fewer.');
      setSubmitting(false);
      return;
    }

    const data = new URLSearchParams();
    const inputs = form.querySelectorAll('input, select, textarea');
    inputs.forEach((input) => {
      const el = input as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (el.name && el.value) data.append(el.name, el.value);
    });

    try {
      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setError('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0B0B0C]/80 dark:bg-[#0B0B0C]/90 backdrop-blur-md px-4 py-8"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white dark:bg-[#1A1A1D] border border-[#E8E8ED] dark:border-[#252528] w-full max-w-lg relative shadow-2xl overflow-y-auto max-h-[90vh]">
        {/* Gold top accent */}
        <div className="h-[2px] bg-gradient-to-r from-[#C9A840] via-[#5BBFB5] to-[#C9A840] w-full" />

        {/* Header */}
        <div className="px-8 py-6 border-b border-[#E8E8ED] dark:border-[#252528] flex items-center justify-between">
          <div>
            <span className="text-[#C9A840] text-xs font-semibold block mb-1" style={{ letterSpacing: '0.2em', textTransform: 'uppercase' }}>
              Auditore AI
            </span>
            <h3 className="text-[#1D1D1F] dark:text-[#F2F0EC] text-xl font-bold tracking-tight">
              Request Consultation
            </h3>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center text-[#6E6E73] dark:text-[#96969E] hover:text-[#1D1D1F] dark:hover:text-[#F2F0EC] transition-colors cursor-pointer border border-[#E8E8ED] dark:border-[#252528] hover:border-[#C9A840]/40"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Body */}
        <div className="px-8 py-8">
          {submitted ? (
            <div className="text-center py-10">
              <div className="w-16 h-16 flex items-center justify-center border-2 border-[#C9A840]/60 mx-auto mb-6 bg-[#C9A840]/5">
                <i className="ri-check-line text-[#C9A840] text-3xl"></i>
              </div>
              <h4 className="text-[#1D1D1F] dark:text-[#F2F0EC] text-xl font-bold mb-3">Inquiry Received</h4>
              <p className="text-[#6E6E73] dark:text-[#96969E] text-sm leading-relaxed max-w-xs mx-auto">
                A member of the Auditore AI team will contact you within one business day to discuss your operational requirements.
              </p>
              <button
                onClick={onClose}
                className="mt-8 px-10 py-3 border border-[#E8E8ED] dark:border-[#252528] text-[#6E6E73] dark:text-[#96969E] text-sm hover:border-[#C9A840]/50 hover:text-[#1D1D1F] dark:hover:text-[#F2F0EC] transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}
              >
                Close
              </button>
            </div>
          ) : (
            <form id="consultation-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                {[{ name: 'first_name', label: 'First Name', placeholder: 'John' }, { name: 'last_name', label: 'Last Name', placeholder: 'Smith' }].map((field) => (
                  <div key={field.name}>
                    <label className="block text-[#6E6E73] dark:text-[#96969E] text-xs mb-2" style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                      {field.label}
                    </label>
                    <input
                      name={field.name}
                      type="text"
                      required
                      placeholder={field.placeholder}
                      className="w-full bg-[#F5F5F7] dark:bg-[#0B0B0C] border border-[#E8E8ED] dark:border-[#252528] text-[#1D1D1F] dark:text-[#F2F0EC] text-sm px-4 py-3 focus:outline-none focus:border-[#C9A840]/60 placeholder-[#C7C7CC] dark:placeholder-[#3A3A3E] transition-colors"
                    />
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-[#6E6E73] dark:text-[#96969E] text-xs mb-2" style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                  Email Address
                </label>
                <input
                  name="email"
                  type="email"
                  required
                  placeholder="john@company.com"
                  className="w-full bg-[#F5F5F7] dark:bg-[#0B0B0C] border border-[#E8E8ED] dark:border-[#252528] text-[#1D1D1F] dark:text-[#F2F0EC] text-sm px-4 py-3 focus:outline-none focus:border-[#C9A840]/60 placeholder-[#C7C7CC] dark:placeholder-[#3A3A3E] transition-colors"
                />
              </div>

              <div>
                <label className="block text-[#6E6E73] dark:text-[#96969E] text-xs mb-2" style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                  Organization
                </label>
                <input
                  name="organization"
                  type="text"
                  required
                  placeholder="Your company name"
                  className="w-full bg-[#F5F5F7] dark:bg-[#0B0B0C] border border-[#E8E8ED] dark:border-[#252528] text-[#1D1D1F] dark:text-[#F2F0EC] text-sm px-4 py-3 focus:outline-none focus:border-[#C9A840]/60 placeholder-[#C7C7CC] dark:placeholder-[#3A3A3E] transition-colors"
                />
              </div>

              <div>
                <label className="block text-[#6E6E73] dark:text-[#96969E] text-xs mb-2" style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                  Industry
                </label>
                <select
                  name="industry"
                  required
                  className="w-full bg-[#F5F5F7] dark:bg-[#0B0B0C] border border-[#E8E8ED] dark:border-[#252528] text-[#1D1D1F] dark:text-[#F2F0EC] text-sm px-4 py-3 focus:outline-none focus:border-[#C9A840]/60 transition-colors cursor-pointer appearance-none"
                >
                  <option value="">Select your industry</option>
                  <option value="Healthcare Clinical Practice">Healthcare Clinical Practice</option>
                  <option value="Construction & Project Delivery">Construction &amp; Project Delivery</option>
                  <option value="Financial Services Institutional">Financial Services Institutional</option>
                  <option value="Lending & Advisory">Lending &amp; Advisory</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-[#6E6E73] dark:text-[#96969E] text-xs mb-2" style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                  Operational Challenge <span className="text-[#C7C7CC] dark:text-[#3A3A3E]">(max 500 chars)</span>
                </label>
                <textarea
                  name="message"
                  rows={4}
                  maxLength={500}
                  placeholder="Briefly describe your primary operational challenge or objective..."
                  className="w-full bg-[#F5F5F7] dark:bg-[#0B0B0C] border border-[#E8E8ED] dark:border-[#252528] text-[#1D1D1F] dark:text-[#F2F0EC] text-sm px-4 py-3 focus:outline-none focus:border-[#C9A840]/60 placeholder-[#C7C7CC] dark:placeholder-[#3A3A3E] transition-colors resize-none"
                />
              </div>

              {error && <p className="text-red-500 dark:text-red-400 text-xs">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="whitespace-nowrap w-full py-4 bg-[#C9A840] text-[#0B0B0C] text-sm font-bold hover:bg-[#A08840] transition-all duration-300 cursor-pointer disabled:opacity-60"
                style={{ letterSpacing: '0.18em', textTransform: 'uppercase' }}
              >
                {submitting ? 'Submitting...' : 'Submit Inquiry'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
